import Vue from "vue";

/**
 * Upload queue of the media service (HCIT-1105). It is global (not part of a component), so uploads go on
 * when the user switches folders or pages; the panel in the app shell shows them (ImgUploadPanel).
 *
 * An item goes WAITING -> UPLOADING (bytes are sent, `progress`) -> IN_PROGRESS (the media service
 * processes it) -> DONE or FAILED (`reason`: TOO_LARGE, UNSUPPORTED, TIMEOUT or the service's text).
 */
export const WAITING = "WAITING";
export const UPLOADING = "UPLOADING";
export const IN_PROGRESS = "IN_PROGRESS";
export const DONE = "DONE";
export const FAILED = "FAILED";
// the file is there already (409): not an error, nothing to retry
export const SKIPPED = "SKIPPED";

export const MAX_SIZE = 10 * 1024 * 1024;

// images and PDF, the media service checks the content
export const ACCEPT = "image/jpeg,image/png,image/gif,image/tiff,image/webp,image/heic,image/heif,application/pdf";
const TYPES = ACCEPT.split(",");
// browsers often report no type for these
const EXTENSIONS = ["heic", "heif", "tif", "tiff"];

export function isSupported (file) {
    const extension = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    return TYPES.includes(file.type) || (!file.type && EXTENSIONS.includes(extension));
}

// Reasons for the HTTP status of a rejected upload; others are SERVER (5xx or unknown), NETWORK (0)
const REASONS = {
    400: "INVALID", 401: "UNAUTHORIZED", 403: "FORBIDDEN", 404: "NOT_FOUND",
    409: "DUPLICATE", 413: "TOO_LARGE", 415: "UNSUPPORTED",
};

/** Reasons where trying the same file again can't work. */
export const NOT_RETRYABLE = ["TOO_LARGE", "UNSUPPORTED", "DUPLICATE", "INVALID", "FORBIDDEN", "UNAUTHORIZED", "NOT_FOUND"];

export function reasonOf (error) {
    const status = error && error.status;
    return REASONS[status] || (status === 0 ? "NETWORK" : "SERVER");
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const isActive = (item) => item.state === WAITING || item.state === UPLOADING || item.state === IN_PROGRESS;

export function createQueue ({ upload, uploadState, concurrency = 3, pollInterval = 1500, pollAttempts = 60 }) {
    const state = Vue.observable({ items: [] });
    const doneListeners = [];
    const finishedListeners = [];
    let uploading = 0;
    let polling = false;
    let key = 0;

    function settle (item, newState, reason, status) {
        item.state = newState;
        item.reason = reason || null;
        item.status = status || null;
        if (newState === DONE) {
            doneListeners.forEach(listener => listener({ siteId: item.siteId, folderId: item.folderId, inspectionId: item.inspectionId, imageType: item.imageType }));
        }
        if (!state.items.some(isActive)) {
            const items = state.items;
            const result = {
                done   : items.filter(i => i.state === DONE).length,
                failed : items.filter(i => i.state === FAILED).length,
                skipped: items.filter(i => i.state === SKIPPED).length,
            };
            finishedListeners.forEach(listener => listener(result));
        }
    }

    function pump () {
        while (uploading < concurrency) {
            const next = state.items.find(item => item.state === WAITING);
            if (!next) {
                return;
            }
            send(next);
        }
    }

    async function send (item) {
        uploading++;
        item.state = UPLOADING;
        item.progress = 0;
        try {
            item.uploadId = await upload(item.siteId, item.folderId, item.file, fraction => item.progress = fraction, item);
            item.progress = 1;
            item.polls = 0;
            item.state = IN_PROGRESS;
            poll();
        } catch (error) {
            const reason = reasonOf(error);
            settle(item, reason === "DUPLICATE" ? SKIPPED : FAILED, reason, error && error.status);
        } finally {
            uploading--;
            pump();
        }
    }

    // One loop for all items the media service is processing
    async function poll () {
        if (polling) {
            return;
        }
        polling = true;
        try {
            while (state.items.some(item => item.state === IN_PROGRESS)) {
                await sleep(pollInterval);
                await Promise.all(state.items.filter(item => item.state === IN_PROGRESS).map(async item => {
                    try {
                        const result = await uploadState(item.uploadId);
                        if (result.status === "DONE") {
                            return settle(item, DONE);
                        }
                        if (result.status === "FAILED") {
                            return settle(item, FAILED, result.reason);
                        }
                    } catch (e) {
                        // try again
                    }
                    if (++item.polls >= pollAttempts) {
                        settle(item, FAILED, "TIMEOUT");
                    }
                }));
            }
        } finally {
            polling = false;
        }
    }

    return {
        state,

        /**
         * Queues [files] for a folder: {siteId, folderId, siteLabel, folderLabel}, or for an inspection:
         * {inspectionId, imageType, siteLabel, folderLabel}.
         */
        add (target, files) {
            // earlier successes and skipped files are gone from the list, failures stay until the user closes them
            if (!state.items.some(isActive)) {
                state.items = state.items.filter(item => item.state === FAILED);
            }

            for (const file of files) {
                const reason = file.size > MAX_SIZE ? "TOO_LARGE" : !isSupported(file) ? "UNSUPPORTED" : null;
                state.items.push({
                    key: ++key, ...target, file,
                    state: reason ? FAILED : WAITING, reason, progress: 0, uploadId: null, polls: 0,
                });
            }
            pump();
        },

        retry (item) {
            if (item.state === FAILED && !NOT_RETRYABLE.includes(item.reason)) {
                item.state = WAITING;
                item.reason = null;
                pump();
            }
        },

        /** Removes finished items (the panel's close). Items still running stay. */
        clear () {
            state.items = state.items.filter(isActive);
        },

        counts () {
            const items = state.items;
            return {
                total     : items.length,
                done      : items.filter(item => item.state === DONE).length,
                failed    : items.filter(item => item.state === FAILED).length,
                skipped   : items.filter(item => item.state === SKIPPED).length,
                active    : items.filter(isActive).length,
            };
        },

        /** [listener]({siteId, folderId, inspectionId, imageType}) for every file the media service has processed. */
        onFileDone (listener) {
            doneListeners.push(listener);
            return () => doneListeners.splice(doneListeners.indexOf(listener), 1);
        },

        /** [listener]({done, failed, skipped}) when nothing is waiting or running any more. */
        onFinished (listener) {
            finishedListeners.push(listener);
            return () => finishedListeners.splice(finishedListeners.indexOf(listener), 1);
        },
    };
}
